// src/render/renderImageBackground.ts
// GSAP-first background seeding: builds the layer + sprite and stamps
// data-* needed by the GSAP igniter. No animation classes or CSS keyframes.

import { resolveImageSrc } from "../utils/resolveAssets";
import { resolveBackgroundPosition, BgDecision } from "../utils/backgroundPositionResolver";
import { Z } from "../utils/constants";

const GSAP = (window as any).gsap as any;

export function renderImageBackground(
  section: HTMLElement,
  attrs: Record<string, any>,
  baseUrl: string
): HTMLElement | null {
  const screenIsVertical = !matchMedia("(orientation: landscape)").matches;
  let imageUrl: string | null = null;
  let assetType: "vertical" | "horizontal" | null = null;
  let source: "background-v" | "background-h" | null = null;

  // 1) Choose asset (v/h with sensible fallback)
  if (screenIsVertical) {
    if (attrs["background-v"]) {
      imageUrl = resolveImageSrc(baseUrl, attrs["background-v"]);
      assetType = "vertical"; source = "background-v";
    } else if (attrs["background-h"]) {
      imageUrl = resolveImageSrc(baseUrl, attrs["background-h"]);
      assetType = "horizontal"; source = "background-h";
    }
  } else {
    if (attrs["background-h"]) {
      imageUrl = resolveImageSrc(baseUrl, attrs["background-h"]);
      assetType = "horizontal"; source = "background-h";
    } else if (attrs["background-v"]) {
      imageUrl = resolveImageSrc(baseUrl, attrs["background-v"]);
      assetType = "vertical"; source = "background-v";
    }
  }

  // generic "background" fallback
  if (!imageUrl && attrs["background"]) {
    imageUrl = resolveImageSrc(baseUrl, attrs["background"]);
    assetType = screenIsVertical ? "vertical" : "horizontal";
    source = null;
  }

  if (!imageUrl) {
    console.warn("[renderImageBackground] No valid image URL resolved", { attrs, baseUrl });
    return null;
  }

  // 2) Build DOM
  const bg = document.createElement("div");
  bg.className = "bg-image-layer";
  bg.setAttribute("aria-hidden", "true");
  bg.style.position = "absolute";
  bg.style.inset = "0";
  bg.style.pointerEvents = "none";
  bg.style.zIndex = String(Z.BACKGROUND);
  if (assetType) bg.dataset.assetType = assetType;
  if (source) bg.dataset.source = source;
  bg.dataset.bgSeeded = "1";

  const sprite = document.createElement("div");
  sprite.className = "bg-anim-sprite";
  sprite.setAttribute("role", "presentation");
  sprite.style.position = "absolute";
  sprite.style.inset = "0";
  sprite.style.backgroundRepeat = "no-repeat";
  sprite.style.backgroundSize = "cover";
  // Lazy-load background images for non-first sections
  const isFirstSection = section.id === "section-0" || section.dataset.sectionIndex === "0";
  if (isFirstSection) {
    sprite.style.backgroundImage = `url(${imageUrl})`;
  } else {
    // Stash URL for lazy assignment
    (sprite as any).dataset.bgSrc = imageUrl;
  }
  sprite.style.pointerEvents = "none";
  sprite.style.willChange = "background-position, transform, opacity";
  // Start paused; GSAP igniter takes control explicitly
  sprite.style.animationPlayState = "paused";

  if (!section.style.position) section.style.position = "relative";
  section.classList.add("bg-image");

  // 3) Decide initial pose and stamp GSAP-friendly data
  const decision: BgDecision = resolveBackgroundPosition(attrs, screenIsVertical, source);

  // Helper: normalize durations to ms string & numeric value
  const toMsString = (v?: string | number, fallback = 20000): string => {
    if (v == null) return `${fallback}ms`;
    if (typeof v === "number") return `${v}ms`;
    return /\d(ms|s)$/.test(v) ? v : `${v}ms`;
  };
  const toEase = (v?: string, fallback = "power1.inOut") => v || fallback;

  if (decision.kind === "slide") {
    // Treat legacy "slide" as background pan
    const start = `${decision.from.x} ${decision.from.y}`;
    const stop  = `${decision.to.x} ${decision.to.y}`;
    const durMs = toMsString(decision.duration, 20000);
    const ease  = toEase(decision.ease, "power1.inOut");

    // Seed static pose at START to avoid pre-ignite jump
    if (GSAP?.set) {
      GSAP.set(sprite, { backgroundPosition: start, transformPerspective: 1000 });
    } else {
      sprite.style.backgroundPosition = start;
    }

    // Stamp data for gsap igniter
    sprite.dataset.bgAnim = "pan";
    sprite.dataset.panStart = start; // "50% 75%"
    sprite.dataset.panStop  = stop;  // "50% 25%"
    sprite.dataset.panDuration = durMs; // "20000ms"
    sprite.dataset.panEase = ease;      // "power1.inOut"

    // Also expose numeric ms (handy if you parseInt)
    sprite.dataset.panDurationMs = String(
      durMs.endsWith("ms") ? parseInt(durMs, 10) : Math.round(parseFloat(durMs) * 1000)
    );
    sprite.dataset.bgPaused = "1";
  } else if (decision.kind === "kenburns") {
    const mode = decision.mode; // "in" | "out"
    const durMs = toMsString(decision.duration, 20000);
    const origin = decision.origin || "50% 50%";
    const startScale = decision.from?.scale || (mode === "in" ? "1" : "1.12");
    const endScale   = decision.to?.scale   || (mode === "in" ? "1.12" : "1");

    if (GSAP?.set) {
      GSAP.set(sprite, {
        transformOrigin: origin,
        scale: parseFloat(String(startScale)),
        backgroundPosition: origin,
        transformPerspective: 1000
      });
    } else {
      sprite.style.transformOrigin = origin;
      (sprite.style as any).transform = `scale(${startScale})`;
      sprite.style.backgroundPosition = origin;
    }

    // Stamp data for gsap igniter
    sprite.dataset.bgAnim = "kenburns";
    sprite.dataset.kbMode = mode; // "in" | "out"
    sprite.dataset.kbDuration = durMs;
    sprite.dataset.kbOrigin = origin;
    sprite.dataset.kbScaleStart = String(startScale);
    sprite.dataset.kbScaleEnd = String(endScale);
    sprite.dataset.bgPaused = "1";
  } else {
    // Static: just honor a resolved position (e.g., "50% 50%")
    sprite.dataset.bgAnim = "static";
    if (GSAP?.set) {
      GSAP.set(sprite, { backgroundPosition: decision.position, transformPerspective: 1000 });
    } else {
      sprite.style.backgroundPosition = decision.position;
    }
    sprite.dataset.bgPaused = "1";
  }

  bg.appendChild(sprite);
  section.appendChild(bg);

  section.dataset.bgSeeded = "1";
  section.dataset.bgIgnited = section.dataset.bgIgnited || "0";

  // If this is not the first section and we have a deferred bg, pre-warm it as the section nears viewport
  try {
    const params = new URLSearchParams(location.search);
    const auditMode = params.has("audit") || params.has("nogate");

    const hasDeferredBg = !!(sprite as any).dataset?.bgSrc;
    if (!isFirstSection && hasDeferredBg) {
      if (auditMode) {
        // In audit/nogate mode, skip preloading entirely to reduce payloads
      } else {
        const rootEl = document.getElementById("scroll-wrapper") || undefined;
        const io = new IntersectionObserver((entries) => {
          if (!entries.some(e => e.isIntersecting)) return;

          const src = (sprite as any).dataset.bgSrc as string;
          if (!src) { io.disconnect(); return; }

          // Prewarm via an Image object, then swap in once decoded
          const img = new Image();
          (img as any).decoding = "async";
          img.src = src;
          img.onload = () => {
            sprite.style.backgroundImage = `url(${src})`;
          };

          // Optional: kick off preload for the next section's bg to mask fast scrolls
          const next = section.nextElementSibling as HTMLElement | null;
          const nextSprite = next?.querySelector<HTMLElement>(".bg-anim-sprite[data-bg-src]");
          const nextSrc = nextSprite?.dataset?.bgSrc;
          if (nextSrc) {
            const n = new Image();
            (n as any).decoding = "async";
            n.src = nextSrc;
          }

          io.disconnect();
        }, { root: rootEl as any, rootMargin: "120% 0px", threshold: 0 });

        io.observe(section);
      }
    }
  } catch (err) {
    console.warn("[renderImageBackground] Lazy bg observer failed", err);
  }

  return bg;
}